class PredictiveSearch extends HTMLElement {
	constructor() {
		super()

		this.selectors = {
			input: '[data-predictive-search-input]',
			form: 'form',
			formStatus: '[data-predictive-search-status]',
			predictiveSearchResults: '[data-predictive-search]',
			closeBtn: '.f-drawer__close'
		}

		this.domNodes = queryDomNodes(this.selectors, this)
		this.drawer = this.closest('drawer-component')
		this.input = this.domNodes.input
		this.form = this.domNodes.form
		this.formStatus = this.domNodes.formStatus
		this.predictiveSearchResults = this.domNodes.predictiveSearchResults
		this.cachedResults = {}
		this.isOpen = false
		this.predictive_search_url = window.FoxThemeSettings.routes.predictive_search_url
		this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
		this.searchTerm = '';

		this.setupEventListeners()
	}

	setupEventListeners() {
		this.form.addEventListener('submit', this.onFormSubmit.bind(this))

		this.input.addEventListener('input', debounce((event) => {
			this.onChange(event)
		}, 300).bind(this))
		this.input.addEventListener('focus', this.onFocus.bind(this))
		this.addEventListener('focusout', this.onFocusOut.bind(this))
		this.addEventListener('keyup', this.onKeyup.bind(this))
		this.addEventListener('keydown', this.onKeydown.bind(this))
		this.domNodes.closeBtn && this.domNodes.closeBtn.addEventListener('click', this.onClose.bind(this))
	}

	getQuery() {
		return this.input.value.trim()
	}

	onChange() {
		const newSearchTerm = this.getQuery();

		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#f-predictive-search-results")?.remove();
    }

		// Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
	}

	onFormSubmit(e) {
		if (!this.getQuery().length || (document.activeElement && document.activeElement.tagName === 'A')) e.preventDefault()
	}
	onFocus() {    
		const currentSearchTerm = this.getQuery();

		if (!currentSearchTerm.length) return;

		if (this.searchTerm !== currentSearchTerm) {
			this.onChange();
		} else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
	}

	onFocusOut(e) {
		setTimeout(() => {
			if (!this.contains(document.activeElement)) this.close();
		})
	}

	onClose(e) {
		this.close(true)
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true)
		event.preventDefault()

		switch (event.code) {
			case 'ArrowUp':
				this.switchOption('up')
				break
			case 'ArrowDown':
				this.switchOption('down')
				break
			case 'Enter':
				this.selectOption()
				break
		}
	}

	onKeydown(e) {
		if (
			e.code === 'ArrowUp' ||
			e.code === 'ArrowDown'
		) {
			e.preventDefault()
		}
	}

	switchOption(direction) {
		if (!this.getAttribute('open')) return

		const moveUp = direction === 'up'
		const selectedElement = this.querySelector('[aria-selected="true"]')
		
		// Harsha ADA: Query all navigable options across all lists (Suggestions, Products, Pages, Keywords)
		const allElements = Array.from(this.querySelectorAll('li, #f-predictive-search-option-search-keywords'))

		if (!allElements.length) return

		let activeElement

		if (!selectedElement) {
			activeElement = moveUp ? allElements[allElements.length - 1] : allElements[0]
		} else {
			const currentIndex = allElements.indexOf(selectedElement)

			if (moveUp) {
				const nextIndex = currentIndex > 0 ? currentIndex - 1 : allElements.length - 1
				activeElement = allElements[nextIndex]
			} else {
				const nextIndex = currentIndex < allElements.length - 1 ? currentIndex + 1 : 0
				activeElement = allElements[nextIndex]
			}
		}

		if (!activeElement || activeElement === selectedElement) return

		this.formStatus.textContent = ''

		// Reset previous selection attribute if present
		if (selectedElement) selectedElement.removeAttribute('aria-selected')

		// Focus management handles highlighting natively

		// Harsha ADA: Focus management
		const activeLink = activeElement.querySelector('a, button')
		if (activeLink) {
			activeLink.focus()
		}

		// Scroll option into view if search drawer overflows
		if (typeof activeElement.scrollIntoView === 'function') {
			activeElement.scrollIntoView({ block: 'nearest' })
		}

		this.setLiveRegionText(activeElement.textContent)
		this.input.setAttribute('aria-activedescendant', activeElement.id)
	}

	selectOption() {
		const activeLink = document.activeElement
		if (activeLink && (activeLink.tagName === 'A' || activeLink.tagName === 'BUTTON')) {
			activeLink.click()
		}
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(" ", "-").toLowerCase()
		this.setLiveRegionLoadingState()

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey])
			return
		}
		const url = `${this.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`
		fetch(url)
			.then((res) => {
				if (!res.ok) {
					var error = new Error(res.status)
					this.close()
					throw error
				}

				return res.text()
			})
			.then((text) => {
				const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML

				// Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );

				this.renderSearchResults(resultsMarkup)
			})
			.catch((error) => {
				this.close()
				throw error
			})
	}

	setLiveRegionLoadingState() {
		this.loadingText = this.loadingText || this.getAttribute('data-loading-text')

		this.setLiveRegionText(this.loadingText)
		this.setAttribute('loading', true)
	}

	setLiveRegionText(statusText) {
			this.formStatus.removeAttribute('aria-hidden')
			this.formStatus.textContent = statusText

			// setTimeout(() => {
			// 	this.formStatus.textContent = ''
			// }, 1000)
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup
		this.setAttribute('results', true)

		this.setLiveRegionResults()
		this.open()
	}

	setLiveRegionResults() {
		this.removeAttribute('loading')
		this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent)
	}

	updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

	open() {
		this.setAttribute('open', true)
		this.input.setAttribute('aria-expanded', true)
		this.isOpen = true

		console.log("🔍 ADA TEST: PredictiveSearch.open() fired. aria-expanded set to true on input.");


		// Harsha ADA: Announce to screen readers that the search popup is active
        if (this.formStatus) {
            this.setLiveRegionText('Search popup opened');
        }

	}

	close(clearSearchTerm = false) {
		console.log(`🔍 ADA TEST: PredictiveSearch.close() running. clearSearchTerm = ${clearSearchTerm}`);

		if (clearSearchTerm) {
			this.input.value = ''
			this.removeAttribute('results')
		}

		const selected = this.querySelector('[aria-selected="true"]')
		if (selected) selected.removeAttribute('aria-selected')

		this.input.setAttribute('aria-activedescendant', '')
		this.removeAttribute('open')
		
		// 🛠️ HARSHA ADA FIX: Check if the actual search drawer element is still open
		const mainDrawer = this.closest('drawer-component, .f-drawer, #Drawer-Search');
		const isDrawerStillOpen = mainDrawer && (
			mainDrawer.hasAttribute('open') || 
			mainDrawer.classList.contains('is-open') || 
			mainDrawer.classList.contains('active')
		);

		// If the drawer is still open, keep aria-expanded as true!
		this.input.setAttribute('aria-expanded', isDrawerStillOpen ? 'true' : 'false');
		console.log(`   └─ aria-expanded set to: ${isDrawerStillOpen ? 'true' : 'false'}`);

		this.predictiveSearchResults.removeAttribute('style')
		this.isOpen = false
	}
}

// Keep everything ABOVE line 238 as is, then use this below:

customElements.define('predictive-search', PredictiveSearch)

/**
 * Harsha ADA: Universal Drawer State Observer
 * Replaces fragmented click handlers and eliminates race conditions.
 * Automatically keeps aria-expanded perfectly synced with drawer open/close states.
 */
document.addEventListener('DOMContentLoaded', () => {
  console.log("🔍 ADA TEST: DOMContentLoaded fired. Setting up drawer MutationObservers...");
  const drawers = document.querySelectorAll('drawer-component, .f-drawer, [id^="Drawer-"]')
  console.log(`🔍 ADA TEST: Found ${drawers.length} drawer elements on the page.`);

  drawers.forEach((drawer) => {
    const observer = new MutationObserver(() => {
      const isOpen = drawer.hasAttribute('open') || 
                     drawer.classList.contains('is-open') || 
                     drawer.classList.contains('active')

	  console.log(`🔍 ADA TEST: Mutation observed on drawer (#${drawer.id || 'No ID'}). Open State: ${isOpen}`);

      const drawerId = `#${drawer.id}`
      const openers = document.querySelectorAll(`drawer-opener[data-drawer="${drawerId}"]`)

      // Update aria-expanded on all triggers tied to this drawer
      openers.forEach((opener) => {
        const button = opener.querySelector('button, [role="button"]')
        if (button) {
          button.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
		  console.log(`   └─ Updated opener button aria-expanded to: ${isOpen ? 'true' : 'false'}`);
        }
      })

	// Harsha ADA: Handle search drawer input expanded state & announcements
	      if (drawer.id === 'Drawer-Search') {
	        const innerInput = drawer.querySelector('input[type="search"], input[type="text"]')
	        
	        if (isOpen) {
	          // Tell screen readers the input/combobox is now active/expanded
	          if (innerInput) {
	            innerInput.setAttribute('aria-expanded', 'true')
				console.log("   └─ Search Input aria-expanded forced to 'true'");
	            innerInput.focus()
				console.log("   └─ Focused structural search input element.");
	          }

	          const predictiveSearchEl = drawer.querySelector('predictive-search')
	          if (predictiveSearchEl && typeof predictiveSearchEl.setLiveRegionText === 'function') {
	            predictiveSearchEl.setLiveRegionText('Search popup opened')
	          }
	        } else {
	          // Tell screen readers the input/combobox is collapsed when drawer closes
	          if (innerInput) {
	            innerInput.setAttribute('aria-expanded', 'false')
				console.log("   └─ Search Input aria-expanded forced to 'false'");
	          }
	        }
	      }
    })

    observer.observe(drawer, { attributes: true, attributeFilter: ['open', 'class'] })
  })

  // Keyboard accessibility for close buttons (Enter / Space)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      const closer = e.target.closest('drawer-closer, .f-drawer__close')
      if (closer) {
		console.log(`🔍 ADA TEST: Close button triggered via keyboard key: "${e.key}"`);
        e.preventDefault()
        closer.click()
      }
    }
  })
})